import { Dialog, DropdonwFlags, Loading } from '@/components';
import { States } from '@/helpers/common/states';
import React, { Fragment, ReactNode } from 'react';
import { Container, Label } from 'reactstrap';
import FilterVector from '@/assets/images/svg/FilterVector';
import { ColumnImage, ColumnStatus, CustomTable } from '@/components/Table';
import Page from '@/model/Page';
import { ActionProps } from '@/components/Dialog';
import Pagination from '@/components/Utils/Pagination';
import Event from '@/model/Event';
import EventStatus from '@/model/EventStatus';
import { colors } from '@/styles/colors';
import dayjs from 'dayjs';
import { FormData, FormErrors, OnChangeFormInput } from '@/hooks';
import { columns, ShouldShowModal } from '../../types';
import { FilterContent } from '../../components/FilterContent';
import { LoadCsvContent } from '../../components/LoadCsvContent';

interface StateProps {
  state: States;
  title: ReactNode;
  visible: boolean;
  shouldShowModal: ShouldShowModal;
  formData: FormData;
  formErrors: FormErrors;
  fileName?: string;
  currentPage: Page<Event, Event>;
}

interface DispatchProps {
  onToggle: () => void;
  onShouldShowModal: ({
    value,
    newTitleModal,
    eventId,
  }: {
    value: ShouldShowModal;
    newTitleModal: string | React.ReactNode;
    eventId?: string;
  }) => void;
  onClearFilter: () => void;
  onChangeFormInput: OnChangeFormInput;
  onFilter: () => void;
  onChangeFileInput: (file: File | undefined) => void;
  loadCsv: () => void;
  onPageChange: (page: number) => void;
}

type Props = StateProps & DispatchProps;

export const EventCloseCsvContainer: React.FC<Props> = ({
  state,
  title,
  visible,
  onToggle,
  shouldShowModal,
  onShouldShowModal,
  formData,
  formErrors,
  onClearFilter,
  onChangeFormInput,
  onFilter,
  fileName,
  onChangeFileInput,
  loadCsv,
  currentPage,
  onPageChange,
}): JSX.Element => {
  const renderActionDialogToCancelFilter: ActionProps = {
    title: 'Limpar',
    onClick: (): void => onClearFilter(),
    theme: 'noneBorder',
  };
  const renderModalActionProps: ActionProps = {
    title: 'Cancelar',
    onClick: (): void => onToggle(),
    theme: 'noneBorder',
  };
  const changeColorColumn = (status: EventStatus): string => {
    let color = colors.grey;
    switch (status) {
      case EventStatus.DRAFT:
        color = colors.lightBlue;
        break;
      case EventStatus.PENDING:
        color = colors.darkRed;
        break;
      case EventStatus.ACTIVE:
        color = colors.green;
        break;
      case EventStatus.REFUSED:
        color = colors.yellow;
        break;
      case EventStatus.BLOCKED:
        color = colors.red;
        break;
      case EventStatus.FINISHED:
        color = colors.lightGreen;
        break;
      case EventStatus.INACTIVE:
        color = colors.orange;
        break;
      case EventStatus.COMPLETED:
        color = colors.grey;
        break;
      default:
        color = colors.grey;
        break;
    }
    return color;
  };
  const dataEventType = [
    { id: 0, name: 'Evento mono' },
    { id: 1, name: 'Evento pai' },
    { id: 2, name: 'Evento filho' },
  ];
  const dataTable =
    currentPage.list && currentPage.list.length > 0
      ? currentPage.list.map(event => ({
          id: event.id,
          image: (
            <ColumnStatus
              justify="right"
              statusColor={String(changeColorColumn(event.eventStatus))}
            >
              <ColumnImage srcImage={event.imageBase64} />
            </ColumnStatus>
          ),
          name: (
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
              }}
            >
              <div>{event.name}</div>
              {
                <DropdonwFlags
                  className="mt-2"
                  dataColumn={dataEventType.filter(value => value.id === event.eventType)}
                />
              }
            </div>
          ),
          city: `${event.address.city}/${event.address.state}`,
          startDate:
            event.startDate === null
              ? '-----'
              : // eslint-disable-next-line no-useless-concat
                `${dayjs(event.startDate).format('DD/MM/YYYY')} às ${String(event.startDate)
                  .split('T')[1]
                  .slice(0, 5)}`,
          endDate:
            event.endDate === null
              ? '-----'
              : `${dayjs(event.endDate).format('DD/MM/YYYY')} às ${String(event.endDate)
                  .split('T')[1]
                  .slice(0, 5)}`,
        }))
      : [];
  return (
    <Fragment>
      <Loading isVisible={state === States.loading} />
      <Dialog
        title={title}
        visible={visible}
        onClose={onToggle}
        position="center"
        isContentWithCard={shouldShowModal !== ShouldShowModal.filter}
        actions={[
          {
            [ShouldShowModal.filter]: renderActionDialogToCancelFilter,
            [ShouldShowModal.file]: renderModalActionProps,
          }[shouldShowModal],
          {
            [ShouldShowModal.filter]: {
              title: 'Aplicar',
              onClick: (): void => onFilter(),
            },
            [ShouldShowModal.file]: {
              title: 'Carregar CSV',
              onClick: (): void => loadCsv(),
            },
          }[shouldShowModal],
        ]}
      >
        {
          {
            [ShouldShowModal.filter]: (
              <FilterContent
                formData={formData}
                formErrors={formErrors}
                onChangeFormInput={onChangeFormInput}
              />
            ),
            [ShouldShowModal.file]: (
              <LoadCsvContent fileName={fileName} onChangeFileInput={onChangeFileInput} />
            ),
          }[shouldShowModal]
        }
      </Dialog>
      <Container className="mainContainer" fluid={true}>
        <div className="d-flex justify-content-between" style={{ paddingBottom: '30px' }}>
          <div className="pageTitle" style={{ display: 'grid' }}>
            <Label>Consolidação de vendas</Label>
          </div>
          <div className="button-filter-container">
            <div className="filter-container">
              <div
                className="filter-content"
                onClick={(): void =>
                  onShouldShowModal({ value: ShouldShowModal.filter, newTitleModal: 'Filtrar' })
                }
              >
                <FilterVector />
              </div>
            </div>
          </div>
        </div>
        <CustomTable
          columns={columns}
          data={dataTable}
          numberRowsPerPage={currentPage.pageSize}
          progressPending={state === States.loading}
          getRowId={e =>
            onShouldShowModal({
              value: ShouldShowModal.file,
              newTitleModal: 'Consolidar CSV',
              eventId: e.id,
            })
          }
        />
        <Pagination
          currentPage={currentPage.page}
          totalCount={currentPage.total}
          pageSize={currentPage.pageSize}
          onPageChange={page => onPageChange(page)}
          total={currentPage.total}
        />
      </Container>
    </Fragment>
  );
};
